/**
 * TimelineScrubber — read-only session timeline (Phase 6.3).
 *
 * Floating bottom-left bar with a tick per AI turn. Clicking a tick scrolls
 * the chat panel to that message; the canvas does NOT time-travel (Phase 0
 * decision recorded in REDESIGN_DECISIONS.md). Bigger sienna ring marks
 * the currently-anchored turn; sienna fill bar shows progress.
 *
 * Visual port of design/kaiva-canvas/project/components/app.jsx
 * TimelineScrubber + the matching `.scrubber*` CSS.
 */

import { cn } from '@/lib/utils';

export interface TimelineEvent {
  /** Matching ChatMessage id — used by the parent to scroll into view. */
  id: string;
  /** Hover tooltip text. */
  label: string;
  /** Optional count rendered as a small "+N" badge above the tick. */
  badge?: number;
}

interface TimelineScrubberProps {
  events: TimelineEvent[];
  /** Index of the currently-anchored event (latest by default). */
  currentIdx: number;
  /** True when the user has scrubbed back from the latest event. */
  isReplaying: boolean;
  onScrub: (idx: number, event: TimelineEvent) => void;
  /** Optional override for the right-side caption (defaults to "Q N of 5"). */
  totalQuestions?: number;
}

export function TimelineScrubber({
  events,
  currentIdx,
  isReplaying,
  onScrub,
  totalQuestions = 5,
}: TimelineScrubberProps) {
  if (events.length < 2) return null;

  const safeIdx = Math.max(0, Math.min(currentIdx, events.length - 1));
  const fillPct = events.length > 1 ? (safeIdx / (events.length - 1)) * 100 : 0;
  const questionIdx = Math.min(totalQuestions, Math.floor(safeIdx / 2) + 1);

  return (
    <div
      className="fixed z-[15]"
      style={{
        left: 24,
        bottom: 78,
        width: 320,
        padding: '10px 14px 8px',
        background: 'hsl(var(--card) / 0.94)',
        backdropFilter: 'blur(8px)',
        border: '1px solid hsl(var(--border))',
        borderRadius: 10,
        boxShadow: 'var(--shadow-1)',
        animation: 'scrubberIn 400ms ease-out',
      }}
      aria-label="Session timeline scrubber"
    >
      <div className="relative mb-1 flex h-5 items-center justify-between">
        {/* Track baseline */}
        <span
          aria-hidden="true"
          className="absolute"
          style={{
            left: 6,
            right: 6,
            top: '50%',
            height: 1.5,
            background: 'hsl(var(--border))',
            transform: 'translateY(-50%)',
            borderRadius: 1,
          }}
        />
        {/* Sienna fill */}
        <span
          aria-hidden="true"
          className="absolute"
          style={{
            left: 6,
            top: '50%',
            height: 1.5,
            width: `calc(${fillPct}% - ${(fillPct / 100) * 12}px)`,
            background: 'hsl(var(--sienna))',
            transform: 'translateY(-50%)',
            borderRadius: 1,
            transition: 'width 400ms ease',
            maxWidth: 'calc(100% - 12px)',
          }}
        />

        {/* Ticks */}
        {events.map((ev, i) => (
          <Tick
            key={ev.id}
            event={ev}
            past={i <= safeIdx}
            now={i === safeIdx}
            onClick={() => onScrub(i, ev)}
          />
        ))}
      </div>

      <div
        className="flex justify-between"
        style={{
          fontSize: 10,
          color: 'hsl(var(--muted-foreground))',
          letterSpacing: '0.02em',
        }}
      >
        <span>Interview start</span>
        <span className="flex items-center gap-1.5">
          {isReplaying && (
            <span
              aria-live="polite"
              className="rounded"
              style={{
                fontSize: 9.5,
                fontWeight: 600,
                color: 'hsl(var(--sienna-deep))',
                background: 'hsl(var(--sienna-soft))',
                padding: '1px 5px',
                letterSpacing: '0.03em',
              }}
            >
              Replaying
            </span>
          )}
          <span>
            {isReplaying ? 'Replay' : 'Now'} · Q{questionIdx} of {totalQuestions}
          </span>
        </span>
      </div>
    </div>
  );
}

function Tick({
  event,
  past,
  now,
  onClick,
}: {
  event: TimelineEvent;
  past: boolean;
  now: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={event.label}
      aria-label={event.label}
      className={cn(
        'group relative z-[2] flex items-center justify-center',
        'cursor-pointer border-0 bg-transparent p-0',
      )}
      style={{ width: 20, height: 20 }}
    >
      {event.badge !== undefined && event.badge > 0 && (
        <span
          aria-hidden="true"
          className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap"
          style={{
            fontSize: 9.5,
            fontWeight: 600,
            color: 'hsl(var(--sienna-deep))',
            background: 'hsl(var(--sienna-soft))',
            padding: '1px 4px',
            borderRadius: 3,
          }}
        >
          +{event.badge}
        </span>
      )}
      <span
        aria-hidden="true"
        className="rounded-full transition-all duration-200 group-hover:scale-110"
        style={{
          width: now ? 11 : 8,
          height: now ? 11 : 8,
          background: past || now ? 'hsl(var(--sienna))' : 'hsl(var(--background))',
          border: `1.5px solid ${
            past || now ? 'hsl(var(--sienna))' : 'hsl(var(--border))'
          }`,
          boxShadow: now ? '0 0 0 3px hsl(var(--sienna) / 0.2)' : undefined,
        }}
      />
    </button>
  );
}
